<template>
  <div>
    <b-card no-body>
      <b-row class="mx-1 mt-2 mb-1" align-h="between">
        <b-col lg="4" md="12" sm="12">
          <b-row align-h="between">
            <b-col lg="4" md="4" sm="12" class="mb-1">
              <div class="form-select">
                Show
                <b-form-select
                  value="10"
                  v-on:change="getSelectedPageSize"
                  :options="options"
                  style="width: 60px;"
                  size="sm"
                ></b-form-select>
                Entries
              </div>
            </b-col>
          </b-row>
        </b-col>
        <b-col lg="2" md="7" sm="7" class="mb-1">
          <ThreatFilter
            v-on:threatFilterChange="threatFilterUpdate"
            :threatOptions="threatOptions"
          >
          </ThreatFilter>
        </b-col>
        <b-col lg="3" md="12" sm="12" class="mb-1">
          <SearchBar :hasErrorMessage="true">
            <template #input>
              <b-form-input v-model="filter" type="search" placeholder="Search" />
            </template>
          </SearchBar>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <div v-if="hasData">
            <b-table
              class="table-with-actions-min-height px-2"
              responsive
              striped
              outlined
              :items="ipsFilter"
              :fields="computedTableFields"
              :current-page="currentPage"
              :filter="filter"
              @filtered="onFiltered"
              :filter-function="filterTable"
              select-mode="single"
              selectable
              @row-selected="onRowSelected"
            >
              <template #head(domains)>
                <div class="d-flex align-items-center">
                  <span>{{ getColumnLabel('domains') }}</span>
                  <b-icon
                    icon="info-circle"
                    class="ml-1"
                    variant="dark"
                    v-b-tooltip.hover.top
                    :title="getDomainNameTooltip"
                  ></b-icon>
                </div>
              </template>
              <template #cell(domains)="row">
                <StringArrayDisplay
                  :items="row.item.domains"
                  :uniqueId="'domains-' + row.item.ip"
                  :propName="getColumnLabel('domains')"
                />
              </template>
              <template #cell(programs)="row">
                <StringArrayDisplay
                  :items="row.item.programs"
                  :uniqueId="'program-' + row.item.ip"
                  :propName="getColumnLabel('programs')"
                  />
              </template>
              <template #cell(deviceCount)="row">
                {{ formatNumberWithCommas(row.value) }}
              </template>
              <template #cell(totalConnectionCount)="row">
                {{ formatNumberWithCommas(row.value) }}
              </template>
              <template #cell(threatFeeds)="row">
                <ThreatFeedIcons
                  :threatFeedInfo="row.item.threatFeeds"
                  :threatFeedTypes="threatFeedTypes"
                  :uniqueId="row.item.ip">
                </ThreatFeedIcons>
              </template>
            </b-table>
            <b-row>
            <b-col>
              <h5
                class="mb-2 mx-1 text-right"
              >
                <span
                  class="total-users-wrapper"
                  v-b-tooltip.hover
                  :title="getTotalIpsTooltip"
                >
                  Total IPs: {{ IpCountInfo.ipsCount }}
                </span>
              </h5>
            </b-col>
            <b-col>
              <ArrowPagination
                :nextPageAvailable="nextPageAvailable"
                :totalItemsCount="IpCountInfo.ipsCount"
                :currentPageCount="ipsInfo.length"
                :paginationParams="paginationParams"
                :sortOptions="paginationSortOptions"
              >
              </ArrowPagination>
            </b-col>
          </b-row>
          </div>
          <div v-else-if="loaded" class="mx-1 pl-1 mb-1">
            {{ $t("No Data Available") }}
          </div>
        </b-col>
      </b-row>
    </b-card>
    <IpDetails :timeRange="timeRange"></IpDetails>
  </div>
</template>

<script>
import axios from 'axios';
import Please from 'pleasejs';

import ArrowPagination from '@/xvisor/components/ArrowPagination.vue';
import formatNumberWithCommas from '@/xvisor/utilities/addCommaToNumber';
import IpDetails from '@/xvisor/components/security/IpDetails.vue';
import SearchBar from '@/xvisor/components/SearchBar.vue';
import SecurityEventBus from '@/xvisor/bus/SecurityEventBus';
import StringArrayDisplay from '@/xvisor/components/security/StringArrayDisplay.vue';
import ThreatFeedIcons from '@/xvisor/components/ThreatFeedIcons.vue';
import ThreatFilter from '@/xvisor/components/security/ThreatFilter.vue';

export default {
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
    countUrl: {
      type: String,
      required: true,
    },
  },
  components: {
    ArrowPagination,
    SearchBar,
    ThreatFeedIcons,
    ThreatFilter,
    StringArrayDisplay,
    IpDetails,
  },
  data() {
    return {
      ipsInfo: [],
      IpCountInfo: {},
      getThreatFeedsUrl: '/security/threatfeeds.json',
      loaded: false,
      selectedThreatFilters: [],
      threatOptions: [],
      currentPage: 1,
      nextPageAvailable: false,
      getNewPitUrl: '/es/pit/createid',
      options: [
        { value: 5, text: '5' },
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
      ],
      threatFeedTypes: {
        all: {
          name: 'Select All',
          type: 'all',
        },
      },
      filter: null,
      filteredLength: null,
      tableFields: [
        { key: 'ip', label: 'Destination IP', sortable: true },
        { key: 'domains', label: 'Domain Name', sortable: true },
        { key: 'deviceCount', label: 'Devices', sortable: true },
        { key: 'programs', label: 'Processes', sortable: false },
        { key: 'totalConnectionCount', label: 'Total Connections', sortable: true },
        { key: 'threatFeeds', label: 'Threat', sortable: true },
      ],
      paginationParams: {
        from: 0,
        size: 10,
        searchAfter: null,
        pitId: '',
        key: 'ip',
        desc: true,
      },
      paginationSortOptions: [
        { label: 'IP Desc.', key: 'ip', desc: true },
        { label: 'IP Asc.', key: 'ip', desc: false },
      ],
      searchAfterDict: {},
      selected: [],
    };
  },
  computed: {
    httpParams() {
      return {
        params: {
          start: this.timeRange.start.toISOString(),
          end: this.timeRange.end.toISOString(),
          size: this.paginationParams.size + 1, // Determine whether to disable next page arrow.
          pitId: this.paginationParams.pitId,
          key: this.paginationParams.key,
          desc: this.paginationParams.desc,
          searchAfter: this.searchAfterDict[this.paginationParams.from],
        },
      };
    },
    getTotalIpsTooltip() {
      return 'Count Of All Unique IPs';
    },
    getDomainNameTooltip() {
      return 'Top Level Domain Names';
    },
    hasData() {
      return this.loaded && this.ipsInfo && this.ipsInfo.length > 0;
    },
    paginationTotalRows() {
      if (this.filter) return this.filteredLength;
      return this.ipsInfo.length;
    },
    computedTableFields() {
      return this.tableFields;
    },
    ipsFilter() {
      this.getThreatTypeAndCount();
      return this.ipsInfo.filter((server) => this.includeBySelectedThreatType(server));
    },
  },
  watch: {
    paginationParams: {
      handler() {
        if (this.paginationParams.pitId !== '') {
          this.loaded = false;
          this.$http.get(this.url, this.httpParams)
            .then((res) => {
              const { results } = res.data;
              if (results.length > this.paginationParams.size) {
                this.nextPageAvailable = true;
                this.ipsInfo = results.slice(0, this.paginationParams.size);
                this.searchAfterDict[this.paginationParams.from
                  + this.paginationParams.size
                ] = this.ipsInfo[this.ipsInfo.length - 1].ip;
              } else {
                this.nextPageAvailable = false;
                this.ipsInfo = results;
              }
            }).catch(() => {
              this.resetPaginationParams();
              this.getPitId();
            }).finally(() => { this.loaded = true; });
        }
      },
      deep: true,
    },
    timeRange() {
      this.resetPaginationParams();
      this.httpGet();
    },
    selected() {
      SecurityEventBus.$emit('update-selected-traffic', this.selected[0]);
    },
  },
  mounted() {
    this.getPitId();
  },
  methods: {
    getPitId() {
      this.$http
        .get(this.getNewPitUrl, {
          params: {
            indexName: 'xtend-network-connection',
            version: 8,
          },
        })
        .then((response) => {
          const { id } = response.data;
          this.paginationParams.pitId = id;
          this.httpGet();
        });
    },
    getSelectedPageSize(selected) {
      this.searchAfterDict = {};
      this.paginationParams.from = 0;
      this.paginationParams.size = selected;
    },
    httpGet() {
      this.loaded = false;
      axios
        .all([
          this.$http.get(this.url, this.httpParams),
          this.$http.get(this.getThreatFeedsUrl),
          this.$http.get(this.countUrl, {
            params: {
              start: this.timeRange.start.toISOString(),
              end: this.timeRange.end.toISOString(),
            },
          }),
        ])
        .then(axios.spread((ipsInfoRes, threatFeedsRes, countRes) => {
          const { results } = ipsInfoRes.data;
          if (results.length > this.paginationParams.size) {
            this.nextPageAvailable = true;
            this.ipsInfo = results.slice(0, this.paginationParams.size);
            this.searchAfterDict[this.paginationParams.from
              + this.paginationParams.size
            ] = this.ipsInfo[this.ipsInfo.length - 1].ip;
          } else {
            this.nextPageAvailable = false;
            this.ipsInfo = results;
          }
          this.IpCountInfo = countRes.data;
          // Store possible threat types in a key-value object.
          threatFeedsRes.data.forEach((val) => { this.threatFeedTypes[val.type] = val; });
          this.getThreatTypeAndCount();
          this.getThreatIconColors();
        }))
        .finally(() => { this.loaded = true; });
    },
    includeBySelectedThreatType(server) {
      if ((!this.selectedThreatFilters.length)
        || (this.selectedThreatFilters[0] === 'all' && server.threatFeeds && server.threatFeeds.length > 0)
        || (server.threatFeeds && this.threatTypeExists(server.threatFeeds))) {
        return true;
      }
      return false;
    },
    threatTypeExists(threatFeeds) {
      let threatMatches = true;
      this.selectedThreatFilters.forEach((threat) => {
        threatMatches = threatFeeds.includes(threat);
      });
      return threatMatches;
    },
    threatFilterUpdate(threatFilters) {
      this.selectedThreatFilters = threatFilters;
      this.filteredLength = this.ipsFilter.length;
      this.currentPage = 1;
    },
    getThreatTypeAndCount() {
      // Determine threat type and their counts after filters are applied.
      const feedTypes = Object.values(this.threatFeedTypes);

      this.threatOptions = feedTypes.map((option) => {
        let count = 0;
        this.ipsInfo.forEach((item) => {
          if (item.threatFeeds && item.threatFeeds.length) {
            if (option.type === 'all') {
              count += 1;
            } else {
              item.threatFeeds.forEach((feed) => {
                if (feed === option.type) {
                  count += 1;
                }
              });
            }
          }
        });
        return { threatName: option.name, threatType: option.type, threatCount: count };
      });
    },
    getThreatIconColors() {
      const feedTypesLength = Object.keys(this.threatFeedTypes).length;
      const hsvs = [
        {
          h: 24,
          s: 1,
          v: 0.5,
        },
        {
          h: 220,
          s: 1,
          v: 0.7,
        },
        {
          h: 264,
          s: 1,
          v: 0.5,
        },
      ];
      const allColors = hsvs.map((hsv) => {
        const scheme = Please.make_scheme(
          hsv,
          {
            scheme_type: 'analogous',
            format: 'hex',
          },
        );
        return scheme;
      }).flat();
      const randomColors = allColors.sort(() => 0.5 - Math.random()).slice(0, feedTypesLength);
      Object.keys(this.threatFeedTypes).forEach((key, index) => {
        this.threatFeedTypes[key].style = randomColors[index];
      });
    },
    onFiltered(filteredItems) {
      this.filteredLength = filteredItems.length;
      this.currentPage = 1;
    },
    onRowSelected(item) {
      this.selected = item;
    },
    resetPaginationParams() {
      this.searchAfterDict = {};
      this.paginationParams = {
        ...this.paginationParams,
        from: 0,
      };
    },
    getColumnLabel(key) {
      return this.tableFields.find((field) => field.key === key).label;
    },
    formatNumberWithCommas,
  },
};
</script>

<style scoped>
.form-select{
  min-width: 160px;
  width: 20%;
}
</style>
